import { type FieldMetadata, useInputControl } from "@conform-to/react";
import {
  Checkbox as HeadlessCheckbox,
  type CheckboxProps as HeadlessCheckboxProps,
} from "@headlessui/react";
import type { VariantProps } from "class-variance-authority";
import { classNames } from "../../utils/classNames";
import { checkboxVariants } from "./variants";

type CheckboxProps = VariantProps<typeof checkboxVariants> &
  HeadlessCheckboxProps & {
    meta?: FieldMetadata<string>;
  };

export function Checkbox({
  className,
  color = "dark/zinc",
  meta,
  onBlur,
  onChange,
  ...props
}: CheckboxProps) {
  const control = meta
    ? // biome-ignore lint/correctness/useHookAtTopLevel: If 'meta' is not provided then 'useInputControl' should not be called at all
      useInputControl(meta)
    : {
        blur: undefined,
        focus: undefined,
        change: undefined,
        value: undefined,
      };

  return (
    <HeadlessCheckbox
      data-slot="control"
      className={classNames(className, "group inline-flex focus:outline-none")}
      onChange={(checked) => {
        control.change?.(checked ? "on" : "off");
        onChange?.(checked);

        setTimeout(() => {
          control.blur?.();
        });
      }}
      defaultValue={control.value}
      defaultChecked={control.value === "on"}
      {...props}
    >
      <span className={checkboxVariants({ color })}>
        <svg
          className="size-4 stroke-[--checkbox-check] opacity-0 group-data-[checked]:opacity-100 sm:h-3.5 sm:w-3.5"
          viewBox="0 0 14 14"
          fill="none"
        >
          {/* Checkmark icon */}
          <path
            className="opacity-100 group-data-[indeterminate]:opacity-0"
            d="M3 8L6 11L11 3.5"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Indeterminate icon */}
          <path
            className="opacity-0 group-data-[indeterminate]:opacity-100"
            d="M3 7H11"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </HeadlessCheckbox>
  );
}
